import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Input, Select, Table } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { ApiError } from '../api/client';
import { listDevices, type DeviceRecord } from '../api/devices';
import { listLayouts, type LayoutRecord } from '../api/layouts';
import { fetchTenantUsage, type TenantUsage } from '../api/tenant';
import {
  attachDevice,
  createScreen,
  deleteScreen,
  detachDevice,
  fetchPresence,
  listScreens,
  patchScreen,
  revokeScreenUrl,
  rotateScreenUrl,
  setScreenUrlMode,
  type ScreenDto,
  type ScreenOrientation,
  type ScreenPresence,
  type ScreenProfile,
  type ScreenStatus,
  type SizeClass,
  type UrlMode
} from '../api/screens';

const PROFILES: ScreenProfile[] = ['home', 'frame', 'desk', 'signage', 'kiosk', 'custom'];
const ORIENTATIONS: ScreenOrientation[] = ['landscape', 'portrait'];
const SIZE_CLASSES: SizeClass[] = ['tablet-1280x800', 'esp32-800x480', 'tv-1920x1080', 'custom'];
const STATUSES: ScreenStatus[] = ['active', 'paused', 'archived'];
const URL_MODES: UrlMode[] = ['off', 'public', 'pin'];

type Draft = {
  name: string;
  profile: ScreenProfile;
  orientation: ScreenOrientation;
  size_class: SizeClass;
  timezone: string;
  url_mode: UrlMode;
  status: ScreenStatus;
  url_pin: string;
  url_expires_at: string;
};

function defaultDraft(): Draft {
  return {
    name: '',
    profile: 'home',
    orientation: 'landscape',
    size_class: 'tablet-1280x800',
    timezone: typeof Intl === 'undefined' ? 'Europe/London' : Intl.DateTimeFormat().resolvedOptions().timeZone,
    url_mode: 'public',
    status: 'active',
    url_pin: '',
    url_expires_at: ''
  };
}

/** Absolute URL a viewer opens (origin + the `/s/<id>` path the server returns). */
function absoluteUrl(path: string | null): string {
  if (!path) return '';
  return typeof window === 'undefined' ? path : `${window.location.origin}${path}`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

/**
 * X1.4 — Screens.
 *
 * List / create / edit screens, drive the capability-URL lifecycle (mode, copy,
 * rotate, revoke), attach devices and watch live viewer presence. Only endpoints
 * documented in `docs/API.md` are called.
 *
 * **QR note:** the road-map uses the vendored `uQR` encoder (D6), but D6 is still
 * PROPOSED and adding a dependency needs the owner's approval, so the QR image is
 * deliberately not built here — the URL is shown as selectable text with Copy and
 * Open. The QR half is the one outstanding clause of X1.4 (recorded in the task).
 */
export function Screens() {
  const { session } = useSession();
  const { notify, notifyError } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';

  const [screens, setScreens] = useState<ScreenDto[]>([]);
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [layouts, setLayouts] = useState<LayoutRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState<Draft>(defaultDraft());
  const [editingName, setEditingName] = useState('');
  const [busy, setBusy] = useState('');
  const [presence, setPresence] = useState<ScreenPresence | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ScreenDto | null>(null);
  const [usage, setUsage] = useState<TenantUsage | null>(null);

  const load = useCallback(async () => {
    try {
      const [rows, deviceRows, layoutRows] = await Promise.all([listScreens(), listDevices(), listLayouts()]);
      setScreens(rows);
      setDevices(deviceRows);
      setLayouts(layoutRows);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load screens.');
    } finally {
      setLoading(false);
    }
  }, []);

  // FB-0.2 / CC-8: usage next to the create form ("3 of 10 screens").
  useEffect(() => {
    let active = true;
    fetchTenantUsage()
      .then((next) => active && setUsage(next))
      .catch(() => active && setUsage(null));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(() => screens.find((row) => row.id === selectedId) ?? null, [screens, selectedId]);

  // Presence for the selected screen (X1.3), polled while selected.
  useEffect(() => {
    if (!selectedId) {
      setPresence(null);
      return;
    }
    let active = true;
    const tick = () => {
      void fetchPresence(selectedId)
        .then((next) => active && setPresence(next))
        .catch(() => active && setPresence(null));
    };
    tick();
    const timer = window.setInterval(tick, 10_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [selectedId]);

  async function create() {
    if (!draft.name.trim()) return;
    setBusy('create');
    try {
      const input = {
        name: draft.name.trim(),
        profile: draft.profile,
        orientation: draft.orientation,
        size_class: draft.size_class,
        timezone: draft.timezone.trim() || 'Europe/London',
        url_mode: draft.url_mode,
        status: draft.status,
        ...(draft.url_mode === 'pin' && draft.url_pin ? { url_pin: draft.url_pin } : {}),
        ...(draft.url_expires_at ? { url_expires_at: new Date(draft.url_expires_at).toISOString() } : {})
      };
      const created = await createScreen(input);
      notify(`Screen “${created.name}” created`);
      setDraft(defaultDraft());
      await load();
      setSelectedId(created.id);
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not create the screen.');
    } finally {
      setBusy('');
    }
  }

  async function saveName() {
    if (!selected || !editingName.trim() || editingName.trim() === selected.name) return;
    try {
      await patchScreen(selected.id, { name: editingName.trim() });
      notify('Renamed');
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not rename.');
    }
  }

  async function changeMode(mode: UrlMode, pin?: string) {
    if (!selected) return;
    setBusy('mode');
    try {
      const updated = await setScreenUrlMode(selected.id, {
        url_mode: mode,
        ...(mode === 'pin' && pin ? { url_pin: pin } : {})
      });
      notify(mode === 'off' ? 'URL turned off' : mode === 'pin' ? 'URL is PIN-protected' : 'URL is public');
      setScreens((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not change the URL mode.');
    } finally {
      setBusy('');
    }
  }

  /**
   * Assign a published layout as the screen's content: a whole-week schedule
   * whose fallback targets the layout. (A richer 7-day grid is X2.3.)
   */
  async function assignLayout(layoutId: string) {
    if (!selected || !layoutId) return;
    const layout = layouts.find((row) => row.id === layoutId);
    const schedule = {
      timezone: selected.timezone,
      blocks: [],
      overrides: [],
      fallback: { kind: 'layout' as const, layout_id: layoutId }
    };
    setBusy('assign');
    try {
      const updated = await patchScreen(selected.id, { schedule });
      setScreens((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      notify(`Assigned “${layout?.name ?? layoutId}”`);
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not assign the layout.');
    } finally {
      setBusy('');
    }
  }

  async function rotate() {
    if (!selected) return;
    try {
      const updated = await rotateScreenUrl(selected.id);
      setScreens((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      notify('Rotated — the old URL 404s immediately');
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not rotate.');
    }
  }

  async function revoke() {
    if (!selected) return;
    try {
      const updated = await revokeScreenUrl(selected.id);
      setScreens((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      notify('Revoked — the URL 404s until rotated');
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not revoke.');
    }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify('URL copied');
    } catch {
      notify('Clipboard unavailable — copy from the field');
    }
  }

  async function attach(deviceId: string) {
    if (!selected || !deviceId) return;
    setBusy('attach');
    try {
      await attachDevice(selected.id, deviceId);
      notify('Device attached');
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not attach.');
    } finally {
      setBusy('');
    }
  }

  async function detach(deviceId: string) {
    if (!selected) return;
    setBusy('attach');
    try {
      await detachDevice(selected.id, deviceId);
      notify('Device detached');
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not detach.');
    } finally {
      setBusy('');
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    try {
      await deleteScreen(deleteTarget.id);
      notify(`Deleted “${deleteTarget.name}”`);
      if (selectedId === deleteTarget.id) setSelectedId('');
      setDeleteTarget(null);
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not delete.');
    }
  }

  const attached = selected ? devices.filter((device) => (device as { screen?: string }).screen === selected.id) : [];
  const attachable = selected ? devices.filter((device) => (device as { screen?: string }).screen !== selected.id) : [];

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / SCREENS</span>
          <h1>Screens</h1>
        </div>
      </header>

      <p className="detail-note">
        A screen is a named display with its own capability URL. Devices and browsers view it; the
        URL is the product&rsquo;s distribution surface — rotate or revoke it at any time.
      </p>

      {loading && <LoadingState label="Loading screens" />}
      {error && <ErrorState title="Screens unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">NEW SCREEN</span>
            {usage && (
              <span className="detail-note" data-usage>
                {usage.screens.used} of {usage.screens.max} screens
              </span>
            )}
          </header>
          <div className="panel-body">
            <div className="screen-form">
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-name">NAME</label>
                <Input id="screen-name" value={draft.name} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, name: event.target.value }))} />
              </div>
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-profile">PROFILE</label>
                <Select id="screen-profile" value={draft.profile} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, profile: event.target.value as ScreenProfile }))}>
                  {PROFILES.map((value) => <option key={value} value={value}>{value}</option>)}
                </Select>
              </div>
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-orientation">ORIENTATION</label>
                <Select id="screen-orientation" value={draft.orientation} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, orientation: event.target.value as ScreenOrientation }))}>
                  {ORIENTATIONS.map((value) => <option key={value} value={value}>{value}</option>)}
                </Select>
              </div>
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-size">SIZE CLASS</label>
                <Select id="screen-size" value={draft.size_class} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, size_class: event.target.value as SizeClass }))}>
                  {SIZE_CLASSES.map((value) => <option key={value} value={value}>{value}</option>)}
                </Select>
              </div>
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-tz">TIMEZONE</label>
                <Input id="screen-tz" value={draft.timezone} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, timezone: event.target.value }))} />
              </div>
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-mode">URL MODE</label>
                <Select id="screen-mode" value={draft.url_mode} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, url_mode: event.target.value as UrlMode }))}>
                  {URL_MODES.map((value) => <option key={value} value={value}>{value}</option>)}
                </Select>
              </div>
              <div className="screen-field">
                <label className="index-label" htmlFor="screen-status">STATUS</label>
                <Select id="screen-status" value={draft.status} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, status: event.target.value as ScreenStatus }))}>
                  {STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}
                </Select>
              </div>
              {draft.url_mode === 'pin' && (
                <div className="screen-field">
                  <label className="index-label" htmlFor="screen-pin">URL PIN (4–32 CHARS)</label>
                  <Input id="screen-pin" type="password" autoComplete="off" value={draft.url_pin} disabled={!writable} onChange={(event) => setDraft((d) => ({ ...d, url_pin: event.target.value }))} />
                </div>
              )}
            </div>
            <div className="settings-actions">
              <Button type="button" disabled={!writable || busy === 'create' || !draft.name.trim()} onClick={() => void create()}>
                {busy === 'create' ? 'Creating…' : 'Create screen'}
              </Button>
            </div>
          </div>
        </section>
      )}

      {!loading && !error && screens.length === 0 && (
        <EmptyStateBlock title="No screens" detail="Create a screen above to mint its capability URL." />
      )}

      {!loading && !error && screens.length > 0 && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">SCREENS / {String(screens.length).padStart(2, '0')}</span>
          </header>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Profile</th>
                <th>Size</th>
                <th>Status</th>
                <th>URL</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {screens.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{row.profile}</td>
                  <td className="mono">{row.size_class}</td>
                  <td>{row.status}</td>
                  <td>
                    {row.url ? (
                      <Badge>{row.url_mode === 'pin' ? 'PIN' : 'public'}</Badge>
                    ) : (
                      <span className="detail-note">{row.url_mode === 'off' ? 'off' : 'revoked'}</span>
                    )}
                  </td>
                  <td>
                    <Button variant="quiet" type="button" onClick={() => { setSelectedId(row.id); setEditingName(row.name); setCopied(false); }}>
                      {selectedId === row.id ? 'Selected' : 'Manage'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      {selected && (
        <section className="panel" data-screen-detail>
          <header className="panel-heading">
            <span className="index-label">SCREEN — {selected.name}</span>
            <Button variant="quiet" type="button" disabled={!writable} onClick={() => setDeleteTarget(selected)}>Delete</Button>
          </header>

          <div className="panel-body">
            <label className="index-label" htmlFor="screen-rename">RENAME</label>
            <div className="screen-inline">
              <Input id="screen-rename" value={editingName} disabled={!writable} onChange={(event) => setEditingName(event.target.value)} />
              <Button variant="quiet" type="button" disabled={!writable || editingName.trim() === selected.name} onClick={() => void saveName()}>Save name</Button>
            </div>

            {/* ---- content: assign a published layout ---- */}
            <label className="index-label" htmlFor="screen-assign">ASSIGN LAYOUT</label>
            <Select
              id="screen-assign"
              value=""
              disabled={!writable || busy === 'assign' || layouts.length === 0}
              onChange={(event) => void assignLayout(event.target.value)}
            >
              <option value="">{layouts.length === 0 ? 'Publish a layout first' : 'Choose a published layout'}</option>
              {layouts
                .filter((layout) => layout.status === 'published')
                .map((layout) => (
                  <option key={layout.id} value={layout.id}>
                    {layout.name ?? layout.id}{layout.version ? ` (v${layout.version})` : ''}
                  </option>
                ))}
            </Select>
            <p className="detail-note">
              {selected.schedule ? 'A schedule is set (fallback: layout). Assigning replaces it.' : 'No content assigned yet.'}
            </p>

            {/* ---- capability URL (X1.2 lifecycle) ---- */}
            <span className="index-label">CAPABILITY URL</span>
            {selected.url ? (
              <>
                <div className="screen-inline">
                  <Input readOnly value={absoluteUrl(selected.url)} aria-label="Screen URL" data-screen-url />
                  <Button variant="quiet" type="button" onClick={() => void copy(absoluteUrl(selected.url))}>{copied ? 'Copied' : 'Copy'}</Button>
                  <a className="button button-quiet" href={selected.url} target="_blank" rel="noreferrer noopener">Open</a>
                </div>
                <p className="detail-note">
                  {selected.url_mode === 'pin' ? 'PIN-protected — viewers enter the PIN once. ' : ''}
                  {selected.url_expires_at ? `Expires ${formatWhen(selected.url_expires_at)}. ` : ''}
                  Anyone with this URL can view the screen until you rotate or revoke it.
                </p>
                <div className="settings-actions">
                  <Button variant="quiet" type="button" disabled={!writable} onClick={() => void rotate()}>Rotate</Button>
                  <Button variant="quiet" type="button" disabled={!writable} onClick={() => void revoke()}>Revoke</Button>
                </div>
              </>
            ) : (
              <p className="detail-note">
                {selected.url_mode === 'off' ? 'The URL is off.' : 'The URL is revoked.'} Set the mode to serve it.
              </p>
            )}

            <div className="screen-inline">
              <label className="index-label" htmlFor="screen-mode-live">MODE</label>
              <Select id="screen-mode-live" value={selected.url_mode} disabled={!writable || busy === 'mode'} onChange={(event) => void changeMode(event.target.value as UrlMode)}>
                {URL_MODES.map((value) => <option key={value} value={value}>{value}</option>)}
              </Select>
              {selected.url_mode === 'pin' && (
                <Button variant="quiet" type="button" disabled={!writable || busy === 'mode'} onClick={() => {
                  const pin = window.prompt('New URL PIN (4–32 characters)');
                  if (pin) void changeMode('pin', pin);
                }}>Set new PIN</Button>
              )}
            </div>

            {/* ---- presence (X1.3) ---- */}
            <span className="index-label">PRESENCE</span>
            <p className="detail-note" data-screen-presence>
              {presence ? `${presence.count} viewer${presence.count === 1 ? '' : 's'} · last seen ${formatWhen(presence.last_seen)}` : 'No viewers'}
            </p>

            {/* ---- attached devices ---- */}
            <span className="index-label">DEVICES ATTACHED / {String(attached.length).padStart(2, '0')}</span>
            {attached.length === 0 ? (
              <p className="detail-note">No devices attached. A browser can still view the URL.</p>
            ) : (
              <Table>
                <thead>
                  <tr><th>Device</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {attached.map((device) => (
                    <tr key={device.id}>
                      <td>{device.name ?? device.id}</td>
                      <td>{device.status ?? '—'}</td>
                      <td>
                        <Button variant="quiet" type="button" disabled={!writable || busy === 'attach'} onClick={() => void detach(device.id)}>Detach</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {attachable.length > 0 && (
              <div className="screen-inline">
                <label className="index-label" htmlFor="screen-attach">ATTACH DEVICE</label>
                <Select id="screen-attach" value="" disabled={!writable || busy === 'attach'} onChange={(event) => void attach(event.target.value)}>
                  <option value="">Choose a device</option>
                  {attachable.map((device) => (
                    <option key={device.id} value={device.id}>{device.name ?? device.id}</option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </section>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete screen"
        confirmWord="DELETE"
        detail={`Delete “${deleteTarget?.name ?? ''}”? Attached devices are detached (they keep their deployment history) and the URL 404s.`}
        confirmLabel="Delete"
        onConfirm={() => void remove()}
        onClose={() => setDeleteTarget(null)}
      />
    </main>
  );
}
